import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import IbanSelection from "../../components/IbanSelection/IbanSelection";
import { ROUTES_PATH } from "../../constants";
import { useCreditProposalResult } from "../../contexts/CreditProposalResultContext";
import { BankreaderAttempts } from "../../interfaces/Bankreaders";
import { NotificationType } from "../../interfaces/NotificationType";
import CreditProposalService from "../../services/CreditProposal";
import TokenService from "../../services/Token";
import { BankreaderIframe } from "./BankreaderStyledComponents";
import { NoDataBkr } from "../../components/NoDataBkr";
import { InvalidIban } from "../../components/InvalidIban";
import { useMerchant } from "../../contexts/MerchantContext";
import { useCustomerClassification } from "../../contexts/CustomerClassificationContext";
import PostMessageService from "../../services/PostMessage";

interface BankreaderProps {
  loadingEvent: (data: boolean) => void;
  returnEvent: (data: boolean) => void;
}

const Bankreader: React.FC<BankreaderProps> = ({
  loadingEvent,
  returnEvent,
}) => {
  let navigate = useNavigate();
  const { creditProposalResult } = useCreditProposalResult();
  const { isNemuru, isPostDataMessages } = useMerchant();
  const { customerClassification } = useCustomerClassification();
  const [bankreaderData, setBankreaderData] = useState<BankreaderAttempts>();
  const [ibanList, setIbanList] = useState<string[]>([]);
  const [showIbanSelector, setShowIbanSelector] = useState(false);
  const [dataMiss, setDataMiss] = useState(false);
  const [invalidIban, setInvalidIban] = useState(false);
  const bankreaderAttempting = useRef<BankreaderAttempts | null>(null);

  const token = TokenService.getToken();

  // const [demoUrl, setDemoUrl] = useState<string>('');

  const createBankreader = async () => {
    if (creditProposalResult) {
      try {
        const BRAttempt = await CreditProposalService.createBankreader(
          creditProposalResult.credit_proposal_id,
          NotificationType.WEB,
        );
        setBankreaderData(BRAttempt);
        bankreaderAttempting.current = BRAttempt;
        window.addEventListener("message", (event) => {
          handleMessage(event, BRAttempt);
        });
      } catch (error) {
        navigate(ROUTES_PATH.UNKNOWN_ERROR);
      } finally {
        loadingEvent(false);
      }
    } else {
      navigate(ROUTES_PATH.UNKNOWN_ERROR);
    }
  };

  const getBankreaderInfo = async () => {
    if (creditProposalResult) {
      try {
        loadingEvent(true);
        if (isPostDataMessages) {
          PostMessageService.BankReaderInitiated(
            creditProposalResult.credit_proposal_id,
          );
        }
        const response = await CreditProposalService.getBankreader(
          creditProposalResult?.credit_proposal_id,
        );
        const finishedAttemps = response.bankreader_attempts.filter(
          (e: BankreaderAttempts) => e.status === "OK",
        );
        let currentAttemp: BankreaderAttempts;
        if (finishedAttemps.length > 0) {
          currentAttemp = finishedAttemps[finishedAttemps.length - 1];
        } else {
          const penddingAttemps = response.bankreader_attempts.filter(
            (e: BankreaderAttempts) => e.status === "PND",
          );
          if (penddingAttemps.length > 0) {
            currentAttemp = penddingAttemps[penddingAttemps.length - 1];
          } else {
            await createBankreader();
            return;
          }
        }
        setBankreaderData(currentAttemp);
        bankreaderAttempting.current = currentAttemp;
        // if (currentAttemp?.url_access) {
        //   const url = currentAttemp.url_access.replace('play', 'demo');
        //   console.log(url);
        //   setDemoUrl(url);
        // }
        if (
          currentAttemp.status === "OK" &&
          currentAttemp.ibans.length > 0 &&
          !invalidIban
        ) {
          setIbanList(currentAttemp.ibans);
          setShowIbanSelector(true);
        } else {
          window.addEventListener("message", (event) => {
            handleMessage(event, currentAttemp);
          });
        }
        returnEvent(false);
        loadingEvent(false);
      } catch (error: any) {
        loadingEvent(false);
        navigate(ROUTES_PATH.UNKNOWN_ERROR);
      }
    } else {
      navigate(ROUTES_PATH.UNKNOWN_ERROR);
    }
  };

  const getIbanList = async (currentAttemp: BankreaderAttempts) => {
    if (creditProposalResult) {
      try {
        loadingEvent(true);
        const response = await CreditProposalService.updateBankreaderOk(
          creditProposalResult?.credit_proposal_id,
          currentAttemp?.bankreader_id,
        );
        if (isNemuru) {
          loadingEvent(true);
          if (
            customerClassification.require_documentation &&
            !creditProposalResult.ocr_id
          ) {
            navigate(ROUTES_PATH.OCR);
          } else {
            navigate(ROUTES_PATH.PAYMENT);
          }
        } else if (response.ibans.length === 0) {
          setInvalidIban(true);
        } else {
          if (response.status === "PEN") {
            setDataMiss(true);
          }
          setIbanList(response.ibans);
          setShowIbanSelector(true);
          loadingEvent(false);
        }
      } catch (error: any) {
        loadingEvent(false);
        navigate(ROUTES_PATH.UNKNOWN_ERROR);
      }
    } else {
      navigate(ROUTES_PATH.UNKNOWN_ERROR);
    }
  };
  const handleMessage = async (
    event: any,
    currentAttemp: BankreaderAttempts,
  ) => {
    if (event.data.type === "BANKREADER_PEPPER") {
      if (event.data.message === "success") {
        if (isPostDataMessages) {
          PostMessageService.BankReaderSuccessful(
            creditProposalResult.credit_proposal_id,
          );
        }
        if (
          currentAttemp?.bankreader_id ===
          bankreaderAttempting.current?.bankreader_id
        ) {
          await getIbanList(currentAttemp);
        }
      } else if (event.data.message === "cancel") {
        if (isPostDataMessages) {
          PostMessageService.BankReaderFailed(
            creditProposalResult.credit_proposal_id,
          );
        }
        navigate(ROUTES_PATH.UNKNOWN_ERROR);
      } else if (event.data.message === "return-button") {
        returnEvent(true);
      } else if (event.data.message === "hide-return-button") {
        returnEvent(false);
      }
    }
  };

  const restartBankreader = () => {
    if (isPostDataMessages) {
      PostMessageService.BankReaderRetried(
        creditProposalResult.credit_proposal_id,
      );
    }
    setShowIbanSelector(false);
    setInvalidIban(false);
    setDataMiss(false);
    createBankreader();
  };

  useEffect(() => {
    getBankreaderInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {bankreaderData && token && !showIbanSelector && !invalidIban && (
        <BankreaderIframe
          title="bankreader-iframe"
          id="pepper-bankreader-iframe"
          src={
            bankreaderData?.version === "V2"
              ? `${bankreaderData?.url_access}&pepperone=true`
              : `${import.meta.env.VITE_BANKREADER_URL}?requestid=${bankreaderData?.bankreader_id}
          &token=${token}&pepperone=true`
          }
        ></BankreaderIframe>
      )}
      {dataMiss && (
        <NoDataBkr
          ibanList={ibanList}
          selectBankreader={restartBankreader}
          loadingEvent={loadingEvent}
        ></NoDataBkr>
      )}
      {invalidIban && (
        <InvalidIban
          selectBankreader={restartBankreader}
          loadingEvent={loadingEvent}
        />
      )}
      {showIbanSelector && ibanList && !invalidIban && (
        <IbanSelection
          ibanList={ibanList}
          loadingEvent={loadingEvent}
        ></IbanSelection>
      )}
    </>
  );
};

export default Bankreader;
